/**
 * Partial-receipt note prompt (card 8reidTon-4MB).
 *
 * Noah 2026-09-30: when a receipt leaves a line short of what was ordered,
 * draft a note ("received 5 of 10 slabs (5 still to come)") and ask whether to
 * save it — the user decides. Shown AFTER a successful receipt, one dialog
 * listing one editable draft per short line: [Skip] [Save notes].
 *
 * - The drafts come from draftPartialReceiptNotes (utils/partialReceiptNotes.ts).
 * - Save appends each non-empty note to purchase_order_line_items.notes through
 *   POST /workflows/purchase-order-receiving/line-items/:id/append-note, which
 *   appends server-side (never overwrites) and is allowed for anyone who may
 *   receive. Card 30nNybLD8IRK: it used to PUT the generated line route, which
 *   needs purchase_orders:update, so warehouse logins could not save.
 *   Skip writes nothing.
 * - Status is not touched (note 0498 rule stays).
 * - Web: PartialReceiptNotesProvider is mounted once in routes.tsx, like
 *   PoStatusRollupProvider, because every receive entry point unmounts
 *   ReceivePurchaseOrderDialog when it closes after the receipt.
 * - Field /field/receive renders PartialReceiptNotesDialog itself (the field
 *   app is a separate route tree).
 */

import { useQueryClient } from '@tanstack/react-query';
import { Loader2, NotebookPen } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

import { notifications } from '../../../config/toast-notifications.js';
import { purchaseOrderLineItemsKeys } from '../../api/generated_hooks/generated-purchase_order_line_items-hooks.js';
import { appendPurchaseOrderLineNote } from '../../api/workflows/purchase-order-receiving.js';
import { HelpHint } from '../help/HelpHint.js';
import { BoulderDialog } from '../ui/boulder-dialog.js';
import { Button } from '../ui/button.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';

import type { PartialReceiptNoteDraft } from './utils/partialReceiptNotes.js';

export interface PartialReceiptNotesRequest {
  poNumber: string;
  drafts: PartialReceiptNoteDraft[];
}

// PO tracker line tables read these, not the generated line-item keys.
const TRACKER_LINE_KEYS = [
  'tracker-purchase-orders-v2',
  'all-line-items-by-color',
  'slab-material-areas',
  'workflow-po-line-items',
];

export function PartialReceiptNotesDialog({
  request,
  onDone,
}: {
  request: PartialReceiptNotesRequest;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [texts, setTexts] = useState(() => request.drafts.map((d) => d.text));
  const [isSaving, setIsSaving] = useState(false);

  const toSave = request.drafts
    .map((d, i) => ({ ...d, text: (texts[i] ?? '').trim() }))
    .filter((d) => d.text !== '');

  const handleSave = async () => {
    if (toSave.length === 0) {return;}
    setIsSaving(true);
    const results = await Promise.allSettled(
      toSave.map((d) => appendPurchaseOrderLineNote(d.lineItemId, d.text)),
    );
    void queryClient.invalidateQueries({ queryKey: purchaseOrderLineItemsKeys.all });
    for (const key of TRACKER_LINE_KEYS) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      notifications.success(
        toSave.length === 1 ? 'Note saved' : 'Notes saved',
        `Added to ${toSave.length === 1 ? 'the line' : `${toSave.length} lines`} on ${request.poNumber}.`,
      );
    } else {
      results.forEach((r) => { if (r.status === 'rejected') {console.error('Partial receipt note save failed:', r.reason);} });
      notifications.error(
        'Some notes were not saved',
        `${failed} of ${toSave.length} could not be saved. Add them from the line's notes.`,
      );
    }
    setIsSaving(false);
    onDone();
  };

  const many = request.drafts.length > 1;

  return (
    <BoulderDialog
      open
      onOpenChange={(o) => { if (!o && !isSaving) { onDone(); } }}
      tone="amber"
      width="lg"
      icon={<NotebookPen className="h-5 w-5" />}
      title={
        <span className="flex items-center gap-2">
          {many ? 'Add partial-receipt notes?' : 'Add a partial-receipt note?'}
          <HelpHint id="receive.partial-note" newTab />
        </span>
      }
      description={
        <>
          <span className="font-semibold text-foreground">{request.poNumber}</span> was only
          partly received. Save {many ? 'a note on each line' : 'a note on the line'} so everyone
          can see what is still to come? Edit the text first if you like; clear a box to leave
          that line alone.
        </>
      }
    >
      <div className="space-y-3">
        {request.drafts.map((d, i) => (
          <div key={d.lineItemId} className="space-y-1">
            <Label htmlFor={`partial-note-${d.lineItemId}`}>{d.materialName}</Label>
            <Textarea
              id={`partial-note-${d.lineItemId}`}
              value={texts[i] ?? ''}
              onChange={(e) =>
                setTexts((prev) => prev.map((t, j) => (j === i ? e.target.value : t)))
              }
              rows={2}
              disabled={isSaving}
            />
          </div>
        ))}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onDone} disabled={isSaving}>
            Skip
          </Button>
          <Button onClick={handleSave} disabled={isSaving || toSave.length === 0}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {toSave.length === 1 ? 'Save note' : 'Save notes'}
          </Button>
        </div>
      </div>
    </BoulderDialog>
  );
}

type PromptPartialReceiptNotes = (request: PartialReceiptNotesRequest) => void;

// Default is a no-op, so a receive dialog rendered outside the provider never prompts.
const PartialReceiptNotesContext = createContext<PromptPartialReceiptNotes>(() => {});

/** Returns promptPartialReceiptNotes(request): call it AFTER a successful receipt. */
export function usePartialReceiptNotesPrompt(): PromptPartialReceiptNotes {
  return useContext(PartialReceiptNotesContext);
}

export function PartialReceiptNotesProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Array<{ seq: number; request: PartialReceiptNotesRequest }>>([]);
  const seq = useRef(0);

  const prompt = useCallback<PromptPartialReceiptNotes>((request) => {
    if (request.drafts.length === 0) {return;}
    seq.current += 1;
    const entry = { seq: seq.current, request };
    setQueue((q) => [...q, entry]);
  }, []);

  const current = queue[0];

  return (
    <PartialReceiptNotesContext.Provider value={prompt}>
      {children}
      {current && (
        <PartialReceiptNotesDialog
          key={current.seq}
          request={current.request}
          onDone={() => setQueue((q) => q.slice(1))}
        />
      )}
    </PartialReceiptNotesContext.Provider>
  );
}
